import { Box, Flex, Grid, Image, Stack, Text, Tooltip } from '@chakra-ui/react'
import CustomTooltip from 'BrokerTsLib/src/components/CustomTooltip'
import type { Part } from 'BrokerTsLib/src/dtos'
import { Insurer, Product, Risk } from 'BrokerTsLib/src/enums'
import { priceFormat } from 'BrokerTsLib/src/Utils/formatPrice'
import type { ReactNode } from 'react'

import type { EditableTotalProps } from '@/containers/EditableTotal'
import { EditableTotal } from '@/containers/EditableTotal'
import { useApp } from '@/hooks/useApp'

import { usePartner } from '@/context/PartnerContext'
import moneyBillIcon from '../../../images/money-bill-duotone-solid.svg'
import warningIcon from '../../../images/warning-icon.svg'


interface OfferPartProps extends Omit<Part, 'name' | 'offerId' | 'description' | 'riskType' | 'valid' | 'included'> {
  name?: string
  label?: ReactNode
  unavailableText?: string
  children?: Part[]
  showPrices?: boolean
  showChildrenPrices?: boolean
  editableProps?: EditableTotalProps
  valid?: boolean
  insurer: Insurer
}

export const OfferPart = (props: OfferPartProps) => {
  const { currency } = usePartner()
  const {
    name,
    nameNew,
    label,
    price,
    unavailableText = 'Není k dispozici',
    errors,
    insurer,
    children,
    riskId,
    showPrices = true,
    showChildrenPrices = false,
    editableProps,
    valid = true
  } = props

  const anyErrors = errors && errors.length > 0
  const formattedPrice =
    (anyErrors || !valid) ? undefined : priceFormat(price, currency?.code)

  const { product } = useApp()

  return (
    <Grid
      sx={{
        p: 2,
        pr: 0,
        gap: 0.5,
        borderBottom: '1px solid',
        borderBottomColor: 'neutral.50',
        _last: {
          borderBottom: 'none',
        }
      }}
    >
      <Flex
        sx={{
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ fontSize: 'sm' }}>{label}</Box>

        {editableProps?.isEditable &&
          <EditableTotal
            size="sm"
            fontWeight='bolder'
            color="transparent"
            {...editableProps}
            price={price ?? undefined}
          />
        }

        {!editableProps?.isEditable && showPrices && formattedPrice
          && (riskId != Risk.BALICEK || insurer != Insurer.Hellas)
          && (
            <Text sx={{ fontSize: 'sm', fontWeight: 'bold' }}>
              {formattedPrice}
            </Text>
          )}

        {!editableProps?.isEditable && showPrices && !formattedPrice && (
          <Text
            sx={{
              color: 'red.500',
              fontSize: 'xs',
              fontWeight: 'bold',
              lineHeight: '1.3125rem',
            }}
          >
            {unavailableText}
          </Text>
        )}
      </Flex>

      <Flex sx={{ width: '100%', justifyContent: 'space-between' }}>
        <Flex gap={2} alignItems='center'>
          <Text sx={{ fontSize: 'sm', fontWeight: 'semibold' }}>{nameNew ?? name}</Text>

          {product == Product.VehicleLeasingInsurance &&
            <OfferPartComplicity {...props} />
          }
        </Flex>

        {!formattedPrice && (anyErrors || !valid) && (
          <Tooltip
            hasArrow
            label={anyErrors ? (
              <Flex direction="column">
                {errors?.map((x) => <Text key={x.message}>{x.message}</Text>)}
              </Flex>
            ) : undefined
            }
            placement="top"
          >
            <Image src={warningIcon} alt="" />
          </Tooltip>
        )}
      </Flex>

      {children &&
        <Stack gap={1}>
          {children.map((p) => (
            <Flex
              key={p.partId ?? p.riskId}
              sx={{ ml: 3, fontSize: 'sm', justifyContent: 'space-between' }}
            >
              <Text>{p.nameNew ?? p.name}</Text>
              {showChildrenPrices && <Text>{priceFormat(p.price, currency?.code, '')}</Text>}
            </Flex>
          ))}
        </Stack>
      }
    </Grid>
  )
}

const OfferPartComplicity = ({ complicityFix, complicityMinimum, complicityPercentage }: OfferPartProps) => {

  if (!complicityFix && !complicityMinimum && !complicityPercentage)
    return null

  return <CustomTooltip hasArrow p={2.5} maxW='20ch'
    trigger='clickHover'
    label={<Stack>
      <Text fontSize='xs' fontWeight='medium'>Spoluúčast</Text>
      <Text fontWeight='semibold'>
        {complicityFix && priceFormat(complicityFix)}
        {complicityPercentage && `${complicityPercentage} %`}
        {complicityMinimum && ` min. ${priceFormat(complicityMinimum)}`}
      </Text>

      {/** TODO: Poznámka ke spoluúčasti */}
      {/* <Text fontSize='sm' fontStyle='oblique' >
         <InfoOutlineIcon h={3} mr={1.5} mb={1} />
          bez spoluúčasti při opravě scelením
        </Text> */}
    </Stack>}>
    <Image src={moneyBillIcon} h={4} w='auto' />
  </CustomTooltip>
}
